import { errorCopy } from '@/api/errorCopy';
/**
 * Automation settings tab — rendered entirely from `GET /me/automation` (TMMAPP-0027).
 *
 * Never hardcode a default, a label, or a key list the API owns. Groups, item labels,
 * descriptions, thresholds and meanings come from the response. Unknown groups title-case
 * as a fallback; unknown `blockedBy` values show via the `other` copy with the raw code.
 *
 * Reset-to-default is omitted: ItemView carries `isDefault` but not the default value itself,
 * so the SPA cannot send the equals-default patch without guessing.
 *
 * TMMAPP-0163 item 6: "How should we name payees?" (`NamingModeSetting`, `/me.payeeNamingMode`)
 * sits under the master switch.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { automationApi, type AutomationCatalog, type AutomationItem } from '@/api/automation';
import { billingApi } from '@/api/billing';
import { entitlementsApi } from '@/api/entitlements';
import {
  aiRefusal,
  ErrorCodes,
  isAiWalletRefusal,
  isApiError,
  type AiRefusal,
  type AiWalletReason,
} from '@/api/errors';
import { AiWalletPrompt } from '@/features/aiWallet/AiWalletPrompt';
import { genesisApi } from '@/api/genesis';
import { ErrorState } from '@/components/ErrorState';
import { aiEnabledPatch, personalLimitPatch } from '@/features/ai/aiLimitPatch';
import { PersonalAiLimit } from '@/features/ai/PersonalAiLimit';
import { NamingModeSetting } from '@/features/payees/NamingModeChoice';
import { patchMe } from '@/api/settings';
import { useMe, useMeRefetch } from '@/session/meHooks';
import type { SettingsNotify } from './settingsHref';

function RereadHistoryButton({ onNotify }: { onNotify: SettingsNotify }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<(AiRefusal & { reason: AiWalletReason }) | null>(null);
  const reread = () => {
    setBusy(true);
    setRefusal(null);
    void genesisApi
      .run()
      .then((result) => {
        onNotify(
          t('genesis.rereadDone', {
            series: result.seriesProposed,
            envelopes: result.envelopesProposed,
          }),
          'success',
        );
      })
      .catch((err) => {
        const ai = aiRefusal(err);
        if (isAiWalletRefusal(ai)) setRefusal(ai);
        else onNotify(errorCopy(t, err, 'automation.errorGeneric'), 'error');
      })
      .finally(() => setBusy(false));
  };
  return (
    <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
      <Button variant="outlined" disabled={busy} onClick={reread}>
        {t('genesis.rereadHistory')}
      </Button>
      {refusal !== null && <AiWalletPrompt refusal={refusal} onTryAgain={reread} />}
    </Stack>
  );
}

interface AutomationTabProps {
  onNotify: SettingsNotify;
}

const BLOCKED_KEYS = ['AI_DISABLED_GLOBAL', 'FEATURE_OFF'] as const;

function titleCaseGroup(raw: string): string {
  return raw
    .split(/[_\s]+/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function groupHeading(raw: string, translated: string, key: string): string {
  if (raw.length === 0) return titleCaseGroup('other');
  if (translated.length > 0 && translated !== key) return translated;
  return titleCaseGroup(raw);
}

function BlockedCaption({ blockedBy }: { blockedBy: string }) {
  const { t } = useTranslation();
  if (blockedBy === 'AI_DISABLED_USER') {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        <Link href="#automation-master-ai">{t('automation.blocked.AI_DISABLED_USER')}</Link>
      </Typography>
    );
  }
  const text = (BLOCKED_KEYS as readonly string[]).includes(blockedBy)
    ? t(`automation.blocked.${blockedBy}`)
    : t('automation.blocked.other', { code: blockedBy });
  return (
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
      {text}
    </Typography>
  );
}

export function AutomationTab({ onNotify }: AutomationTabProps) {
  const { t } = useTranslation();
  const me = useMe();
  const refetchMe = useMeRefetch();
  const [catalog, setCatalog] = useState<AutomationCatalog | null>(null);
  const [loadError, setLoadError] = useState<{ error: unknown } | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  /** The limit's currency (the subscription's); `undefined` while the price book is loading. */
  const [planCurrency, setPlanCurrency] = useState<string | null | undefined>(undefined);
  /** The plan's maximum AI spend (MM-D-134), in the wallet's currency; `null` when unread. */
  const [planMaximum, setPlanMaximum] = useState<{ minor: number; currency: string } | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [stale, setStale] = useState(false);

  const reload = useCallback(async () => {
    try {
      const next = await automationApi.get();
      setCatalog(next);
      setLoadError(null);
      return next;
    } catch (error) {
      setLoadError({ error });
      return null;
    }
  }, []);

  useEffect(() => {
    if (me.user.id === null) return;
    let live = true;
    queueMicrotask(() => {
      if (!live) return;
      void reload();
      void entitlementsApi
        .get()
        .then((ents) => {
          if (!live) return;
          const currency = ents.prices?.currency ?? null;
          setPlanCurrency(currency != null && currency.length > 0 ? currency : null);
        })
        .catch(() => {
          if (live) setPlanCurrency(null);
        });
    });
    return () => {
      live = false;
    };
  }, [me.user.id, reload]);

  // The plan's maximum (MM-D-134) names the "no personal limit" choice; unread, it goes unnamed.
  // The meter is the one `/me/billing` serves (Settings' Billing card reads the same profile).
  const readsMaximum = me.user.id !== null && me.user.aiEnabled;
  useEffect(() => {
    if (!readsMaximum) return;
    let live = true;
    void billingApi
      .profile()
      .then(({ aiWallet: wallet }) => {
        if (!live) return;
        setPlanMaximum(
          wallet !== null ? { minor: wallet.maxAiSpendMinor, currency: wallet.currency } : null,
        );
      })
      .catch(() => {
        if (live) setPlanMaximum(null);
      });
    return () => {
      live = false;
    };
  }, [readsMaximum]);

  if (me.user.id === null) {
    return <Typography variant="body1">{t('consent.noProfile')}</Typography>;
  }

  async function saveAi(patch: {
    aiEnabled?: boolean;
    aiMonthlyCapMinor?: number;
    aiContributionsExcluded?: boolean;
  }) {
    if (aiBusy || Object.keys(patch).length === 0) return;
    setAiBusy(true);
    try {
      await patchMe(patch);
      await refetchMe();
      await reload();
    } catch {
      onNotify(t('automation.errorGeneric'), 'error');
      throw new Error('ai-limit');
    } finally {
      setAiBusy(false);
    }
  }

  async function handleItemToggle(item: AutomationItem, next: boolean) {
    if (rowBusy !== null || catalog === null) return;
    setRowBusy(item.key);
    setStale(false);
    const previous = catalog;
    setCatalog({
      ...catalog,
      groups: catalog.groups.map((g) => ({
        ...g,
        items: g.items.map((row) => (row.key === item.key ? { ...row, enabled: next } : row)),
      })),
    });
    try {
      const updated = await automationApi.patch({ items: [{ key: item.key, enabled: next }] });
      setCatalog(updated);
    } catch (err) {
      setCatalog(previous);
      if (isApiError(err) && err.code === ErrorCodes.AUTOMATION_KEY_UNKNOWN) {
        setStale(true);
        await reload();
        onNotify(t('automation.staleCatalogue'), 'error');
      } else {
        onNotify(t('automation.errorGeneric'), 'error');
      }
    } finally {
      setRowBusy(null);
    }
  }

  async function handleThreshold(item: AutomationItem, raw: string) {
    if (rowBusy !== null || catalog === null) return;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return;
    setRowBusy(item.key);
    try {
      const updated = await automationApi.patch({
        items: [{ key: item.key, thresholdBp: parsed }],
      });
      setCatalog(updated);
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.AUTOMATION_KEY_UNKNOWN) {
        setStale(true);
        await reload();
        onNotify(t('automation.staleCatalogue'), 'error');
      } else {
        onNotify(t('automation.errorGeneric'), 'error');
      }
    } finally {
      setRowBusy(null);
    }
  }

  if (loadError !== null && catalog === null) {
    return (
      <ErrorState
        error={loadError.error}
        what={t('errors.what.automation')}
        onRetry={() => void reload()}
      />
    );
  }

  if (catalog === null) {
    return <Typography variant="body2">{t('common.loading')}</Typography>;
  }

  const showSeeded = me.user.aiEnabledSource === 'DEFAULT' && me.user.aiEnabled;

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      {stale && <Alert severity="warning">{t('automation.staleCatalogue')}</Alert>}
      <Box id="automation-master-ai">
        <Typography variant="h3" gutterBottom>
          {t('automation.masterAiTitle')}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={me.user.aiEnabled}
              disabled={aiBusy}
              onChange={(event) =>
                void saveAi(aiEnabledPatch(me.user.aiEnabled, event.target.checked)).catch(() => {})
              }
              slotProps={{ input: { 'aria-busy': aiBusy || undefined } }}
            />
          }
          label={t('plan.aiOn')}
        />
        <Typography variant="body2" color="text.secondary">
          {t('automation.masterAiHelp')}
        </Typography>
        {me.user.aiEnabled && planCurrency === undefined && (
          <Typography variant="body2">{t('common.loading')}</Typography>
        )}
        {me.user.aiEnabled && planCurrency != null && (
          <Box sx={{ mt: 2 }}>
            <PersonalAiLimit
              currency={planCurrency}
              capMinor={me.user.aiMonthlyCapMinor}
              planMaximum={planMaximum}
              busy={aiBusy}
              onSave={(next) => saveAi(personalLimitPatch(me.user.aiMonthlyCapMinor, next))}
            />
          </Box>
        )}
        <Link component={RouterLink} to="/me/ai-requests" sx={{ display: 'inline-block', mt: 1 }}>
          {t('automation.aiHistoryLink')}
        </Link>
        {/* TMMAPP-0142 item 9 (MM-D-146): the contributor opt-out, `PATCH /v1/me`. */}
        <Box sx={{ mt: 2 }} data-contributions-excluded="">
          <FormControlLabel
            control={
              <Switch
                checked={me.user.aiContributionsExcluded}
                disabled={aiBusy}
                onChange={(event) => {
                  const next = event.target.checked;
                  void saveAi({ aiContributionsExcluded: next })
                    .then(() =>
                      onNotify(
                        t(
                          next
                            ? 'assistant.contributions.savedOn'
                            : 'assistant.contributions.savedOff',
                        ),
                      ),
                    )
                    .catch(() => {});
                }}
                slotProps={{
                  input: {
                    'aria-busy': aiBusy || undefined,
                    'aria-describedby': 'automation-contributions-help',
                  },
                }}
              />
            }
            label={t('assistant.contributions.label')}
          />
          <Typography id="automation-contributions-help" variant="body2" color="text.secondary">
            {t('assistant.contributions.help')}
          </Typography>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h3" gutterBottom>
            {t('genesis.rereadHistory')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('genesis.rereadHistoryBody')}
          </Typography>
          <RereadHistoryButton onNotify={onNotify} />
        </Box>
        {showSeeded && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('consent.aiSeeded')}
          </Typography>
        )}
      </Box>

      <NamingModeSetting onNotify={onNotify} />

      {catalog.groups.map((group) => (
        <Box key={group.group || 'ungrouped'}>
          <Typography variant="h3" gutterBottom>
            {groupHeading(
              group.group,
              t(`automation.group.${group.group}`, { defaultValue: '' }),
              `automation.group.${group.group}`,
            )}
          </Typography>
          <Stack spacing={2}>
            {group.items.map((item) => {
              const blocked = item.blockedBy !== null;
              return (
                <Box key={item.key}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    {!blocked ? (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={item.enabled}
                            disabled={rowBusy === item.key}
                            onChange={(event) => void handleItemToggle(item, event.target.checked)}
                            slotProps={{ input: { 'aria-label': item.label } }}
                          />
                        }
                        label={item.label}
                      />
                    ) : (
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {item.label}
                      </Typography>
                    )}
                    {!item.isDefault && <Chip size="small" label={t('automation.overridden')} />}
                  </Stack>
                  {item.description.length > 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {item.description}
                    </Typography>
                  )}
                  {blocked && item.blockedBy !== null && (
                    <BlockedCaption blockedBy={item.blockedBy} />
                  )}
                  {item.thresholdBp !== null && !blocked && (
                    <TextField
                      size="small"
                      type="number"
                      sx={{ mt: 1, maxWidth: 200 }}
                      label={item.thresholdMeaning ?? t('automation.threshold')}
                      defaultValue={item.thresholdBp}
                      onBlur={(event) => void handleThreshold(item, event.target.value)}
                      helperText={item.thresholdMeaning ?? undefined}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
